import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ASSESSMENT_TYPE_CL003_CODES } from "configs/clinical-codes.constants";
import { Observable } from "rxjs";
import QAActionDetails from "../../components/qa-control/models/qa-action-details";
import QaQueueActionUpdate from "../../components/qa-control/models/qa-queue-action-update";
import { QaQueueComments } from "../../components/qa-control/models/qa-queue-comments";
import { ActionTypeResponse } from "../../components/qa-control/models/qa-queue-opensearch-response";
import QaQueueUsersUpdate from "../../components/qa-control/models/qa-queue-users-update";
import { ConfigurationService } from "../configuration/configuration.service";

export type HopeTypeFilter = "admission" | "huv1" | "huv2" | "sfv";
@Injectable()
export class QaQueueService {
  private readonly qaQueueUrl: string;
  private readonly idgDataUrl: string;

  constructor(public http: HttpClient, private config: ConfigurationService) {
    if (this.config.getConfig().isLocal) {
      this.qaQueueUrl = config.getConfig().localEndpoints.qaQueueUrl;
      this.idgDataUrl = config.getConfig().localEndpoints.idgUrl;
    } else {
      this.qaQueueUrl = config.getConfig().apiUrl;
      this.idgDataUrl = config.getConfig().apiUrl + "/idg";
    }
  }

  searchQAQueueActions(
    tenantId: number,
    entity: string,
    query: string,
    sortBy: string,
    sort: string,
    pageSize: number,
    page: number
  ): Observable<ActionTypeResponse> {
    let params = new HttpParams()
      .set("entity", entity)
      .set("query", query)
      .set("sortBy", sortBy)
      .set("sort", sort)
      .set("pageSize", pageSize.toString())
      .set("page", page.toString());
    return this.http.get<ActionTypeResponse>(
      `${
        this.qaQueueUrl
      }/qa-queue-functions/tenant/${tenantId.toString()}/search`,
      { params: params }
    );
  }

  putActionTypes(
    tenantId: number,
    payload: Array<QaQueueActionUpdate>
  ): Observable<any> {
    return this.http.put(
      `${
        this.qaQueueUrl
      }/qa-queue-functions/tenant/${tenantId.toString()}/actions`,
      payload
    );
  }

  putUsers(tenantId: number, payload: QaQueueUsersUpdate): Observable<any> {
    return this.http.put(
      `${
        this.qaQueueUrl
      }/qa-queue-functions/tenant/${tenantId.toString()}/users`,
      payload
    );
  }

  getCommentCategories(tenantId: number): Observable<any> {
    return this.http.get(
      `${
        this.qaQueueUrl
      }/qa-queue-functions/tenant/${tenantId.toString()}/comment-categories`
    );
  }

  getActionLatestComments(
    tenantId: number,
    actionType: string,
    actionIds: string[]
  ): Observable<QaQueueComments> {
    let commaSeparatedIds = actionIds.join();

    let params = new HttpParams().set("ids", commaSeparatedIds);
    return this.http.get<QaQueueComments>(
      `${this.qaQueueUrl}/qa-queue-functions/tenant/${tenantId}/action/${actionType}/latest-comments`,
      { params: params }
    );
  }

  putComment(
    tenantId: number,
    action: string,
    actionId: string | number,
    category: string,
    comment: string
  ): Observable<any> {
    const payload = { comment, category };
    return this.http.put(
      `${this.qaQueueUrl}/qa-queue-functions/tenant/${tenantId}/action/${action}/${actionId}/comment`,
      payload
    );
  }

  approve(
    tenantId: number,
    action: string,
    actionId: string | number,
    category?: string,
    comment?: string
  ): Observable<any> {
    const payload = category != null ? { comment, category } : null;
    return this.http.post(
      `${this.qaQueueUrl}/qa-queue-functions/tenant/${tenantId}/action/${action}/${actionId}/approve`,
      payload
    );
  }

  returnToClinician(
    tenantId: number,
    action: string,
    actionId: string | number,
    category: string,
    comment: string
  ): Observable<any> {
    const payload = { comment, category };
    return this.http.post(
      `${this.qaQueueUrl}/qa-queue-functions/tenant/${tenantId}/action/${action}/${actionId}/return`,
      payload
    );
  }

  getQAVisits(
    team: number,
    sortBy: string,
    order: "asc" | "desc",
    hopeTypes: HopeTypeFilter[],
    visitTypes: string[]
  ): Observable<any> {
    let params = new HttpParams()
      .set("sort", sortBy)
      .set("order", order)
      .set("limit", "1000");

    if (hopeTypes.length) {
      params = params.set("hopeTypes", hopeTypes.join(","));
    }

    if (visitTypes.length) {
      params = params.set("visitTypes", visitTypes.join(","));
    }

    const url = `${this.idgDataUrl}/qa-queue/submitted-to-qa/team/${team}`;
    return this.http.get(url, { params });
  }

  getActionDetails(
    tenantId: number,
    actionType: string,
    actionId: string
  ): Observable<QAActionDetails> {
    return this.http.get<QAActionDetails>(
      `${this.qaQueueUrl}/qa-queue-functions/tenant/${tenantId}/action-type/${actionType}/action/${actionId}`
    );
  }

  markAsInProgress(
    tenantId: number,
    action: string,
    actionId: string | number
  ): Observable<any> {
    return this.http.put(
      `${this.qaQueueUrl}/qa-queue-functions/tenant/${tenantId}/action/${action}/${actionId}/in-progress`,
      {}
    );
  }

  getAssessmentCodeByValue(value: string): string | null {
    const search = (obj: any): string | null => {
      for (const key of Object.keys(obj)) {
        const item = obj[key];

        // If this level has CODE/VALUE fields
        if (item && typeof item === "object" && "VALUE" in item) {
          if (item.VALUE === value) {
            return item.CODE;
          }
        }

        // If nested object, search deeper
        if (item && typeof item === "object") {
          const result = search(item);
          if (result) return result;
        }
      }
      return null;
    };

    return search(ASSESSMENT_TYPE_CL003_CODES);
  }

  getQaQueueVisitTypesFilterOptions(visitTypes: Array<{ actionName: string }>) {
    return visitTypes
      .map((item) => {
        const code = this.getAssessmentCodeByValue(item.actionName);
        return { actionName: item.actionName, code };
      })
      .filter((item) => item.code !== null); // filter out all null code for Symptom Follow-up Visit and HOPE Update Visit
  }
}



// WEBPACK FOOTER //
// ./src/providers/qa-queue/qa-queue.service.ts